import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Clock,
  Euro,
  MapPin,
  ArrowRight,
  Phone,
  MessageSquare,
} from "lucide-react";

/**
 * SEO-optimized service landing page for local Brussels search.
 * Routes: /nettoyage-canape-bruxelles, /nettoyage-matelas-bruxelles,
 * /nettoyage-tapis-bruxelles, /nettoyage-sieges-auto-bruxelles
 */

const SERVICES = {
  canape: {
    slug: "nettoyage-canape-bruxelles",
    h1: "Nettoyage de canapé à Bruxelles",
    title: "Nettoyage de canapé à Bruxelles · dès 80€ · Pro-pre",
    metaDesc:
      "Nettoyage professionnel de canapé à domicile à Bruxelles avec technologie Kärcher injection-extraction. Tissu, alcantara, microfibre · Séchage 2-6h · Défi de la Bande gratuit 30×30 cm.",
    intro:
      "Votre canapé accumule chaque jour poussière, acariens, sueur et taches invisibles. Notre technologie Kärcher injection-extraction extrait la saleté en profondeur des fibres, sans détremper le rembourrage. Séchage rapide 2 à 6 heures.",
    prices: [
      { label: "Canapé 2 places", price: "80€" },
      { label: "Canapé 3 places", price: "110€" },
      { label: "Canapé d'angle / méridienne", price: "150€" },
    ],
    benefits: [
      "Élimination acariens, bactéries, allergènes",
      "Tissus, cuir synthétique, alcantara, microfibre",
      "Produits sans danger enfants & animaux",
      "Résultat visible avant paiement (Défi de la Bande)",
    ],
    faq: [
      {
        q: "Combien de temps pour nettoyer un canapé 3 places ?",
        a: "En moyenne 45 à 60 minutes sur place. Séchage complet 2 à 6h selon l'aération.",
      },
      {
        q: "Vous nettoyez le cuir aussi ?",
        a: "Le cuir véritable demande un traitement spécifique — contactez-nous pour un devis dédié. Nous traitons parfaitement le cuir synthétique, l'alcantara et la microfibre.",
      },
      {
        q: "Le tissu risque-t-il de rétrécir ?",
        a: "Non. L'injection-extraction utilise de l'eau à basse pression avec extraction immédiate. Le rembourrage reste intact.",
      },
    ],
  },
  matelas: {
    slug: "nettoyage-matelas-bruxelles",
    h1: "Nettoyage de matelas à Bruxelles",
    title: "Nettoyage de matelas à Bruxelles · dès 65€ · Pro-pre",
    metaDesc:
      "Nettoyage de matelas anti-acariens à domicile à Bruxelles. Kärcher injection-extraction · Élimination des taches, sueur, urine, sang · Défi de la Bande gratuit.",
    intro:
      "Vous passez un tiers de votre vie sur votre matelas. Il concentre acariens, cellules mortes, sueur, taches. Notre procédé Kärcher injection-extraction assainit en profondeur sans humidifier durablement le matelas.",
    prices: [
      { label: "Matelas 1 place", price: "65€" },
      { label: "Matelas 2 places", price: "90€" },
      { label: "Matelas surdimensionné", price: "sur devis" },
    ],
    benefits: [
      "Anti-acariens : idéal pour allergiques & asthmatiques",
      "Élimination taches sang, sueur, urine, café",
      "Sans détremper : dormable la nuit même",
      "Traitement recommandé 1× par an",
    ],
    faq: [
      {
        q: "Le matelas sera-t-il utilisable le soir même ?",
        a: "Oui. Nous extrayons plus de 95% de l'eau injectée. Séchage résiduel 2 à 4h avec bonne aération.",
      },
      {
        q: "Vous traitez les deux faces ?",
        a: "Sur demande, oui — nous facturons le second côté à 50% du prix. La plupart de nos clients ne font qu'une face.",
      },
    ],
  },
  tapis: {
    slug: "nettoyage-tapis-bruxelles",
    h1: "Nettoyage de tapis à Bruxelles",
    title: "Nettoyage de tapis à Bruxelles · 20€/m² · Pro-pre",
    metaDesc:
      "Nettoyage de tapis à domicile à Bruxelles au m². Laine, synthétique, berbère, kilim. Kärcher injection-extraction · Séchage rapide · Défi gratuit 30×30.",
    intro:
      "Poussière, poils d'animaux, taches, odeurs : votre tapis mérite mieux qu'un simple aspirateur. Nous nettoyons directement chez vous, sans démonter ni transporter. 20€ par mètre carré.",
    prices: [
      { label: "Tapis (au m²)", price: "20€" },
      { label: "Escalier moquette (par marche)", price: "7€" },
      { label: "Tapis précieux (soie, kilim)", price: "sur devis" },
    ],
    benefits: [
      "Directement à votre domicile — pas de transport",
      "Fibres synthétiques et naturelles (laine, coton)",
      "Élimination odeurs animaux, cigarette, humidité",
      "Séchage 4 à 8h selon épaisseur",
    ],
    faq: [
      {
        q: "Vous prenez les tapis persans / précieux ?",
        a: "Nous les traitons uniquement après un Défi de la Bande sur une zone discrète, pour valider la stabilité des couleurs. Sur devis dédié.",
      },
      {
        q: "Il faut déplacer les meubles ?",
        a: "Nous pouvons vous aider à décaler les meubles principaux. Prévoyez un espace de travail dégagé.",
      },
    ],
  },
  "sieges-auto": {
    slug: "nettoyage-sieges-auto-bruxelles",
    h1: "Nettoyage de sièges auto à Bruxelles",
    title: "Nettoyage de sièges auto à Bruxelles · 100€ · Pro-pre",
    metaDesc:
      "Nettoyage intérieur de voiture à domicile à Bruxelles : sièges, moquettes, ciel de toit. Idéal chauffeurs VTC, Uber, taxi. Kärcher injection-extraction.",
    intro:
      "Vous conduisez pour Uber, Bolt, Heetch, taxi ? Votre voiture, c'est votre outil de travail. Un intérieur impeccable = meilleures notes et plus de courses. Nettoyage complet des 5 sièges chez vous.",
    prices: [
      { label: "Intérieur complet (jusqu'à 5 sièges)", price: "100€" },
      { label: "Ciel de toit", price: "+30€" },
      { label: "Coffre + moquettes", price: "+20€" },
    ],
    benefits: [
      "Idéal chauffeurs VTC (Uber, Bolt, taxi)",
      "Élimination taches café, gras, vomi, animaux",
      "Rafraîchissement odeurs cigarette / transpiration",
      "Intervention 60 à 90 minutes sur place",
    ],
    faq: [
      {
        q: "Vous venez sur mon parking / dans ma rue ?",
        a: "Oui, à condition d'avoir accès électrique (prise 220V) à moins de 20m. Sinon, nous convenons d'un point de rendez-vous.",
      },
      {
        q: "Ça vaut le coup pour un chauffeur VTC ?",
        a: "Absolument. 100€ tous les 2-3 mois vs. 0,3-0,5 étoile de moins en moyenne = investissement rentabilisé en quelques courses.",
      },
    ],
  },
};

export default function ServicePage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const key = Object.keys(SERVICES).find((k) => SERVICES[k].slug === slug);
  const service = key ? SERVICES[key] : null;

  if (!service) {
    return (
      <div className="section text-center">
        <h1 className="text-2xl font-semibold text-navy">Service introuvable</h1>
        <p className="mt-2 text-slate-500">Consultez tous nos services depuis la page d&apos;accueil.</p>
        <Link to="/" className="btn-primary mt-4 inline-flex">Retour à l&apos;accueil</Link>
      </div>
    );
  }

  const canonical = `https://www.pro-pre.com/${service.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.h1,
    provider: { "@id": "https://www.pro-pre.com/#business" },
    areaServed: { "@type": "City", name: "Bruxelles" },
    description: service.metaDesc,
    offers: service.prices.map((p) => ({
      "@type": "Offer",
      name: p.label,
      priceCurrency: "EUR",
      price: p.price.replace(/[^\d.]/g, "") || null,
    })),
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-4" data-testid={`service-page-${key}`}>
      <Helmet>
        <title>{service.title}</title>
        <meta name="description" content={service.metaDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={service.title} />
        <meta property="og:description" content={service.metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="fr_FR" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="section pb-4">
        <div className="container-narrow">
          <div className="text-xs uppercase tracking-widest text-[#5BA4D4] font-bold flex items-center gap-2">
            <MapPin className="h-3 w-3" /> Bruxelles · Ixelles · Uccle · Saint-Gilles · Etterbeek · Schaerbeek
          </div>
          <h1 className="mt-2 text-3xl md:text-5xl font-bold text-navy leading-tight">
            {service.h1}
          </h1>
          <p className="mt-4 text-slate-700 max-w-2xl leading-relaxed">{service.intro}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/booking" className="btn-primary" data-testid="service-cta-booking">
              Réserver en ligne <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/defi" className="btn-outline" data-testid="service-cta-defi">
              <Sparkles className="h-4 w-4" /> Défi de la Bande — gratuit
            </Link>
          </div>

          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
            <a href="tel:+32466132839" className="flex items-center gap-1.5 hover:text-navy">
              <Phone className="h-3.5 w-3.5" /> +32 466 13 28 39
            </a>
            <a
              href="https://wa.me/32466132839"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:text-navy"
            >
              <MessageSquare className="h-3.5 w-3.5" /> WhatsApp direct
            </a>
          </div>
        </div>
      </section>

      {/* Prix */}
      <section className="section pt-6 pb-6">
        <div className="container-narrow">
          <h2 className="text-2xl font-semibold text-navy flex items-center gap-2">
            <Euro className="h-5 w-5 text-[#5BA4D4]" /> Tarifs {service.h1.replace("Nettoyage de ", "").replace("Nettoyage ", "").toLowerCase()}
          </h2>
          <div className="mt-4 grid gap-2">
            {service.prices.map((p) => (
              <div key={p.label} className="card-clean p-4 flex justify-between items-center">
                <span className="text-navy">{p.label}</span>
                <span className="font-bold text-[#5BA4D4]">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500 italic">
            Frais de déplacement offerts dans Bruxelles-Capitale. Périphérie : +10 à +20€ selon distance.
          </p>
        </div>
      </section>

      {/* Bénéfices */}
      <section className="section pt-6 pb-6 bg-slate-50">
        <div className="container-narrow">
          <h2 className="text-2xl font-semibold text-navy flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#5BA4D4]" /> Pourquoi Pro-pre ?
          </h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {service.benefits.map((b) => (
              <div key={b} className="flex items-start gap-2 bg-white p-3 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Défi de la Bande */}
      <section className="section pt-6 pb-6">
        <div className="container-narrow">
          <div className="card-clean p-6 bg-gradient-to-br from-[#5BA4D4]/10 to-white border-[#5BA4D4]/30">
            <div className="flex items-start gap-3">
              <Sparkles className="h-6 w-6 text-[#5BA4D4] flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-navy">Défi de la Bande — gratuit 30×30 cm</h2>
                <p className="mt-1 text-sm text-slate-700">
                  Vous ne payez que si vous voyez la différence. Nous nettoyons une bande de 30×30 cm gratuitement, chez vous. Vous jugez, puis vous décidez.
                </p>
                <Link to="/defi" className="btn-primary mt-3 text-sm" data-testid="service-defi-btn">
                  Demander mon test gratuit <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section pt-6 pb-10">
        <div className="container-narrow">
          <h2 className="text-2xl font-semibold text-navy">Questions fréquentes</h2>
          <div className="mt-4 space-y-3">
            {service.faq.map((f) => (
              <details key={f.q} className="card-clean p-4 group" data-testid={`service-faq-${f.q.slice(0,20)}`}>
                <summary className="cursor-pointer font-semibold text-navy list-none flex items-start justify-between gap-3">
                  <span>{f.q}</span>
                  <span className="text-[#5BA4D4] group-open:rotate-45 transition text-xl leading-none">+</span>
                </summary>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section pt-4 pb-12 bg-navy text-white">
        <div className="container-narrow text-center">
          <Clock className="h-8 w-8 text-[#5BA4D4] mx-auto" />
          <h2 className="mt-3 text-2xl font-bold">Réservation en 2 minutes</h2>
          <p className="mt-2 text-white/80 max-w-lg mx-auto">
            Nouveau client ? Utilisez le code <b className="text-white">VOISIN20</b> pour -20€ sur votre première commande.
          </p>
          <Link to="/booking" className="btn-primary mt-4 inline-flex" data-testid="service-cta-final">
            Prendre rendez-vous <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
